import { useRef, useEffect } from 'react'
import { BarArrayVisualizer } from '@code-arcade/engine'
import type { BarArrayState } from '@code-arcade/engine'
import { useGameStore } from '@/stores/game.store'
import type { VisualizationStep } from '@code-arcade/engine'

interface AlgorithmCanvasProps {
  visualizationType: 'bar-array'
}

export function AlgorithmCanvas({ visualizationType }: AlgorithmCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const visualizerRef = useRef<BarArrayVisualizer | null>(null)
  // Stable ref to current step so the ResizeObserver callback can re-render without stale closure
  const currentStepRef = useRef<VisualizationStep<BarArrayState> | undefined>(undefined)
  const { stepIndex, steps } = useGameStore()

  // Keep step ref in sync
  useEffect(() => {
    currentStepRef.current = steps[stepIndex] as VisualizationStep<BarArrayState> | undefined
  }, [stepIndex, steps])

  // Initialize visualizer; re-render on every resize (setting canvas dimensions clears it)
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    if (visualizationType === 'bar-array') {
      visualizerRef.current = new BarArrayVisualizer(canvas)
    }

    const resize = () => {
      canvas.width = canvas.offsetWidth
      canvas.height = canvas.offsetHeight
      if (visualizerRef.current && currentStepRef.current) {
        visualizerRef.current.render(currentStepRef.current)
      }
    }

    resize()
    const observer = new ResizeObserver(resize)
    observer.observe(canvas)
    return () => observer.disconnect()
  }, [visualizationType])

  // Render whenever step changes
  useEffect(() => {
    const vis = visualizerRef.current
    const step = steps[stepIndex] as VisualizationStep<BarArrayState> | undefined
    if (vis && step) {
      vis.render(step)
    }
  }, [stepIndex, steps])

  return (
    // Absolute fill: parent must be relative. This is more reliable than h-full in flex contexts.
    <div className="absolute inset-0">
      <canvas
        ref={canvasRef}
        className="w-full h-full block"
        style={{ background: '#0a0a0f' }}
      />
    </div>
  )
}
